import { FC, PropsWithChildren } from "react";

import Stack from "@mui/material/Stack";
import { Locales, OrganizationCountry } from "@simplyk/common";
import { useQuery } from "@tanstack/react-query";

import { loadTranslationLabels } from "../helpers/loadTranslationLabels";

import { TranslationModalLayout } from "./TranslationModalLayout";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardSkeleton } from "@/dashboard-design-system/Skeleton";
import { DashboardTypography } from "@/dashboard-design-system/Typography";

interface TranslationLocaleResourcesProps {
  formLocale: Locales;
  translationLocale: Locales;
  organizationCountry?: OrganizationCountry;
  onClose: () => void;
}

export const TranslationLocaleResources: FC<PropsWithChildren<TranslationLocaleResourcesProps>> = ({
  children,
  formLocale,
  translationLocale,
  organizationCountry,
  onClose,
}) => {
  const { t, i18n } = useTranslate();
  const labels = useQuery({
    queryKey: ["translation-dialog-labels", formLocale, translationLocale],
    queryFn: ({ signal }) => loadTranslationLabels(i18n, [formLocale, translationLocale], signal),
    staleTime: Infinity,
  });

  if (labels.isSuccess) {
    return children;
  }

  return (
    <TranslationModalLayout
      open
      onClose={onClose}
      onSubmit={(event) => event.preventDefault()}
      onSwap={() => {}}
      formLocale={formLocale}
      translationLocale={translationLocale}
      organizationCountry={organizationCountry}
      actionsDisabled
      isTranslating={labels.isPending}
      slots={{
        content: labels.isError ? (
          <Stack gap={2}>
            <DashboardTypography role="alert">{t("common", "unknownErrorRetryOrContactUs")}</DashboardTypography>
            <DashboardButton onClick={() => void labels.refetch()}>
              {t("dashboard", "translation.modal.retry")}
            </DashboardButton>
          </Stack>
        ) : (
          <DashboardSkeleton variant="rounded" sx={(theme) => ({ height: theme.spacing(30) })} />
        ),
      }}
    />
  );
};
